import { makeAutoObservable, runInAction } from 'mobx';
import type MainStore from './MainStore';
import { apiService } from '../services/apiService';
import {
  DailyStat,
  DailyStatsResponse,
  FileHistory,
  FileHistoryDetail,
  UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS,
  UploadHistoryTableColumnId,
  UploadHistoryTableColumnSetting,
} from '../types/claims';
import type { SortingState } from '../types/table';
import {
  loadPersistedDashboardFilters,
  savePersistedDashboardFilters,
} from '../components/helpers/dashboardFiltersStorage';

const getDate30DaysAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
};

export const QUICK_DATE_FILTERS = ['today', 'this_week', 'last_week', 'this_month', 'last_month', 'this_year'] as const;

const isQuickDateFilter = (value: string): boolean =>
  (QUICK_DATE_FILTERS as readonly string[]).includes(value);

// Date range for a quick filter, resolved against today. Unknown values fall
// back to the last 30 days.
export const getQuickDateFilterRange = (filterValue: string): { start: Date; end: Date } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  let startDate: Date;

  switch (filterValue) {
    case 'today':
      startDate = new Date(today);
      break;
    case 'this_week': {
      startDate = new Date(today);
      // Get Monday of current week
      const dayOfWeek = today.getDay();
      const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust when day is Sunday
      startDate.setDate(diff);
      break;
    }
    case 'last_week': {
      // Get Monday of this week first
      const thisWeekMonday = new Date(today);
      const lastWeekDayOfWeek = today.getDay();
      const lastWeekDaysFromMonday = lastWeekDayOfWeek === 0 ? 6 : lastWeekDayOfWeek - 1; // Monday = 0, Sunday = 6
      thisWeekMonday.setDate(today.getDate() - lastWeekDaysFromMonday);
      thisWeekMonday.setHours(0, 0, 0, 0);

      // Last week Monday is 7 days before this week's Monday
      startDate = new Date(thisWeekMonday);
      startDate.setDate(thisWeekMonday.getDate() - 7);

      // Last week Sunday is 6 days after last week's Monday
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;
    }
    case 'this_month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'last_month': {
      // Get first day of last month
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      // Get last day of last month
      const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      endDate = new Date(lastDayOfLastMonth);
      endDate.setHours(23, 59, 59, 999);
      break;
    }
    case 'this_year':
      startDate = new Date(today.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
  }

  return { start: startDate, end: endDate };
};

// Formats a Date as YYYY-MM-DD using local timezone components, so every
// consumer (store, drawer, table) derives the same calendar date from the same Date object.
export const formatLocalDate = (date: Date): string => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default class UploadHistoryStore {
  // Date range
  dateRange: { start: Date; end: Date } = {
    start: getDate30DaysAgo(),
    end: new Date()
  };

  // Data
  dailyStats: DailyStat[] | null = null;
  summary: DailyStatsResponse['summary'] | null = null;
  fileList: FileHistory[] | null = null;
  selectedFile: FileHistoryDetail | null = null;

  // UI state
  isLoading: boolean = false;
  isDataLoading: boolean = false; // For table loading
  error: string | null = null;

  // Reprocess (admin) state
  isReprocessing: boolean = false;
  reprocessMessage: string | null = null;
  
  // File list pagination
  fileListPage: number = 1;
  fileListPageSize: number = 500;
  fileListTotal: number = 0;
  fileListTotalPages: number = 0;

  // Filters
  statusFilter: string | null = null;
  ediTypeFilter: string | null = null;
  fileNameFilter: string | null = null;
  claimNumberFilter: string | null = null;
  selectedDateFilter: string | null = null; // 'today', 'last_week', 'last_month', or null
  // True when dateRange was picked by hand (column filter / All Filters drawer)
  // rather than by a quick filter or the 30-day default.
  isCustomDateRange: boolean = false;

  // Sorting (sent to backend)
  sorting: SortingState = [{ id: 'uploaded_at', desc: true }];

  // Column settings state (visibility + pin/required), keyed by column id.
  // Defaults come from UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS[].required.
  columnSettings: UploadHistoryTableColumnSetting[] = UploadHistoryStore.getDefaultColumnSettings();

  mainStore: MainStore;

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, { mainStore: false });
    this.mainStore = mainStore;
    this.restorePersistedFilters();
  }

  // Filters and column settings are persisted together under one localStorage
  // key per tenant and user (see dashboardFiltersStorage), restored again by
  // MainStore.setFiltersScope once the tenant and user are known. A quick filter's range is recomputed
  // against today; a custom range keeps its dates.
  restorePersistedFilters = () => {
    const persisted = loadPersistedDashboardFilters(this.mainStore.filtersScope);

    this.statusFilter = persisted.statusFilter;
    this.ediTypeFilter = persisted.ediTypeFilter;
    this.fileNameFilter = persisted.fileNameFilter;
    this.claimNumberFilter = persisted.claimNumberFilter;
    this.columnSettings = UploadHistoryStore.mergeColumnSettings(persisted.columnSettings);
    if (persisted.sorting && persisted.sorting.length > 0) {
      this.sorting = persisted.sorting;
    }

    if (persisted.selectedDateFilter && isQuickDateFilter(persisted.selectedDateFilter)) {
      this.selectedDateFilter = persisted.selectedDateFilter;
      this.dateRange = getQuickDateFilterRange(persisted.selectedDateFilter);
    } else if (persisted.customDateRange) {
      this.dateRange = {
        start: new Date(persisted.customDateRange.start),
        end: new Date(persisted.customDateRange.end),
      };
      this.isCustomDateRange = true;
    }
  };

  private savePersistedFilters(): void {
    savePersistedDashboardFilters(this.mainStore.filtersScope, {
      selectedDateFilter: this.selectedDateFilter,
      customDateRange:
        !this.selectedDateFilter && this.isCustomDateRange
          ? { start: this.dateRange.start.toISOString(), end: this.dateRange.end.toISOString() }
          : null,
      statusFilter: this.statusFilter,
      ediTypeFilter: this.ediTypeFilter,
      fileNameFilter: this.fileNameFilter,
      claimNumberFilter: this.claimNumberFilter,
      columnSettings: this.columnSettings,
      sorting: this.sorting,
    });
  }

  // Default column settings, derived from UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS[].required
  private static getDefaultColumnSettings(): UploadHistoryTableColumnSetting[] {
    return (UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS ?? []).map(col => ({
      id: col.id as UploadHistoryTableColumnId,
      visible: true,
      required: col.required === true,
    }));
  }

  // Merge saved column settings with defaults to handle new/removed columns
  private static mergeColumnSettings(
    saved: UploadHistoryTableColumnSetting[] | null
  ): UploadHistoryTableColumnSetting[] {
    const defaults = UploadHistoryStore.getDefaultColumnSettings();
    if (!saved) return defaults;
    const savedById = new Map<string, UploadHistoryTableColumnSetting>(
      saved.filter(setting => setting && typeof setting === 'object').map(setting => [setting.id, setting])
    );
    return defaults.map(def => ({ ...def, ...savedById.get(def.id) }));
  }

  private getColumnSetting = (columnId: string): UploadHistoryTableColumnSetting | undefined =>
    this.columnSettings.find(setting => setting.id === columnId);

  private updateColumnSetting = (columnId: UploadHistoryTableColumnId, updates: Partial<Omit<UploadHistoryTableColumnSetting, 'id'>>) => {
    this.columnSettings = this.columnSettings.map(setting =>
      setting.id === columnId ? { ...setting, ...updates } : setting
    );
    this.savePersistedFilters();
  };

  setColumnVisibility = (columnId: UploadHistoryTableColumnId, visible: boolean) => {
    // uploaded_at is always required
    if (columnId === 'uploaded_at') return;
    this.updateColumnSetting(columnId, { visible });
  };

  toggleColumnVisibility = (columnId: UploadHistoryTableColumnId) => {
    // uploaded_at is always required
    if (columnId === 'uploaded_at') return;
    this.updateColumnSetting(columnId, { visible: !this.getColumnSetting(columnId)?.visible });
  };

  isColumnVisible = (columnId: string): boolean => {
    // uploaded_at is always visible
    if (columnId === 'uploaded_at') return true;
    return this.getColumnSetting(columnId)?.visible !== false;
  };

  toggleRequiredColumn = (columnId: UploadHistoryTableColumnId) => {
    // uploaded_at is always required
    if (columnId === 'uploaded_at') return;
    const nextRequired = !this.isColumnRequired(columnId);
    // Marking a hidden column as required makes it visible so it has a visible effect
    this.updateColumnSetting(columnId, nextRequired ? { required: true, visible: true } : { required: false });
  };

  isColumnRequired = (columnId: string): boolean => {
    // uploaded_at is always required
    if (columnId === 'uploaded_at') return true;
    return !!this.getColumnSetting(columnId)?.required;
  };

  get hasMore() {
    return !!(this.fileList && this.fileListPage < this.fileListTotalPages - 1);
  }

  // Visible column ids in table display order: pinned (required) columns
  // first, then the rest, each in UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS order.
  get visibleColumnIds(): UploadHistoryTableColumnId[] {
    const visible = UPLOAD_HISTORY_TABLE_COLUMN_OPTIONS
      .map((col) => col.id as UploadHistoryTableColumnId)
      .filter((id) => this.isColumnVisible(id));
    return [
      ...visible.filter((id) => this.isColumnRequired(id)),
      ...visible.filter((id) => !this.isColumnRequired(id)),
    ];
  }

  // Filter + sort query params for the file list — shared by loadFileList and
  // the CSV export so the file holds exactly what the table shows.
  get fileListQueryParams() {
    const sortConfig = this.sorting[0];
    return {
      start_date: formatLocalDate(this.dateRange.start),
      end_date: formatLocalDate(this.dateRange.end),
      ...(this.statusFilter && { status: this.statusFilter }),
      ...(this.ediTypeFilter && { edi_object_type: this.ediTypeFilter }),
      ...(this.fileNameFilter && { file_name: this.fileNameFilter }),
      ...(this.claimNumberFilter && { claim_number: this.claimNumberFilter }),
      ...(sortConfig?.id && {
        sort_by: sortConfig.id,
        order: (sortConfig.desc ? 'desc' : 'asc') as 'asc' | 'desc',
      }),
    };
  }

  // Actions
  setDateRange = (start: Date, end: Date) => {
    this.dateRange = { start, end };
    this.savePersistedFilters();
  };

  setStatusFilter = (status: string | null) => {
    this.statusFilter = status;
    this.savePersistedFilters();
  };

  setEdiTypeFilter = (ediType: string | null) => {
    this.ediTypeFilter = ediType;
    this.savePersistedFilters();
  };

  setFileNameFilter = (fileName: string | null) => {
    this.fileNameFilter = fileName && fileName.trim() ? fileName.trim() : null;
    this.savePersistedFilters();
  };

  setClaimNumberFilter = (claimNumber: string | null) => {
    this.claimNumberFilter = claimNumber && claimNumber.trim() ? claimNumber.trim() : null;
    this.savePersistedFilters();
  };

  setSelectedDateFilter = (filter: string | null) => {
    this.selectedDateFilter = filter;
    if (filter) this.isCustomDateRange = false;
    this.savePersistedFilters();
  };

  // Selects a quick filter and sets its date range.
  applyQuickDateFilter = (filter: string) => {
    this.setSelectedDateFilter(filter);
    const { start, end } = getQuickDateFilterRange(filter);
    this.setDateRange(start, end);
  };

  // Applies a custom start/end date range (YYYY-MM-DD strings) and clears the active quick filter.
  // No-ops if the range is invalid (start after end).
  applyCustomDateRange = (startDateStr: string, endDateStr: string) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    if (startDate <= endDate) {
      this.isCustomDateRange = true;
      this.setDateRange(startDate, endDate);
      this.setSelectedDateFilter(null);
    }
  };

  // Resets the date range back to the default (last 30 days) and clears the active quick filter.
  clearDateRangeFilter = () => {
    this.isCustomDateRange = false;
    this.setDateRange(getDate30DaysAgo(), new Date());
    this.setSelectedDateFilter(null);
  };

  setSorting = (sorting: SortingState) => {
    this.sorting = sorting.length > 0 ? sorting : [{ id: 'uploaded_at', desc: true }];
    this.savePersistedFilters();
  };

  loadDailyStats = async () => {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });
    
    try {
      // Use graph date range if available (30 days from first date in table),
      // otherwise use the regular date range
      const rangeToUse = this.graphDateRange || this.dateRange;

      const startDate = formatLocalDate(rangeToUse.start);
      const endDate = formatLocalDate(rangeToUse.end);
      
      const response = await apiService.getUploadHistoryDaily(
        startDate,
        endDate,
        this.statusFilter || undefined,
        this.ediTypeFilter || undefined
      );
      
      runInAction(() => {
        this.dailyStats = response.daily_stats;
        this.summary = response.summary;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load daily statistics';
      });
      console.error('Error loading daily stats:', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Graph date range (30 days from first date in table)
  graphDateRange: { start: Date; end: Date } | null = null;

  // Calculate date range from first date in table (30 days from first date)
  // This is used for graphs, not for table filtering
  calculateGraphDateRangeFromTable = () => {
    if (!this.fileList || this.fileList.length === 0) {
      return false; // Return false if no files to calculate from
    }

    // Find the earliest date_uploaded in the fileList
    const dates = this.fileList
      .map(file => file.date_uploaded)
      .filter(date => date != null && date !== '')
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));

    if (dates.length === 0) {
      return false;
    }

    const firstDate = new Date(Math.min(...dates.map(d => d.getTime())));
    firstDate.setHours(0, 0, 0, 0);

    // Calculate end date (30 days from first date)
    const endDate = new Date(firstDate);
    endDate.setDate(endDate.getDate() + 30);
    endDate.setHours(23, 59, 59, 999);

    // Update graph date range
    const newGraphRange = { start: firstDate, end: endDate };
    const rangeChanged = 
      !this.graphDateRange ||
      this.graphDateRange.start.getTime() !== firstDate.getTime() ||
      this.graphDateRange.end.getTime() !== endDate.getTime();
    
    if (rangeChanged) {
      runInAction(() => {
        this.graphDateRange = newGraphRange;
      });
      return true; // Return true if range was updated
    }
    
    return false;
  };

  loadFileList = async (date?: string, page: number = 1, skipDateRangeUpdate: boolean = false, isLoadMode?: boolean, isRefresh?: boolean) => {
    runInAction(() => {
      this.isDataLoading = true;
      this.isLoading = true;
      this.error = null;
    });
    
    try {
      const { start_date, end_date, ...filters } = this.fileListQueryParams;
      const params: any = {
        page: isRefresh ? 1 : page,
        page_size: isRefresh ? this.fileList?.length || this.fileListPageSize : this.fileListPageSize,
        ...(date ? { date } : { start_date, end_date }),
        ...filters,
      };

      const response = await apiService.getUploadHistoryFiles(params);
      
      // Debug logging
      console.log('API Response:', {
        params,
        filesCount: response.files.length,
        total: response.pagination.total,
        files: response.files.slice(0, 3).map(f => ({
          date_uploaded: f.date_uploaded,
          file_name: f.file_name
        }))
      });
      
      runInAction(() => {
        this.fileList = isLoadMode ? [...(this.fileList || []), ...response.files || []] : response.files;
        this.fileListPage = response.pagination.page;
        this.fileListPageSize = response.pagination.page_size;
        this.fileListTotal = response.pagination.total;
        this.fileListTotalPages = response.pagination.total_pages;
      });

      // Always calculate graph date range from first date in table (30 days from first date)
      // This ensures graphs always show 30 days from the first date, regardless of filters.
      // graphDateRange is only consumed by loadDailyStats — it must never be mirrored back into
      // dateRange, or the table's own filter (params just sent above) would stop matching what
      // the Drawer/Table display.
      if (!skipDateRangeUpdate) {
        const graphRangeUpdated = this.calculateGraphDateRangeFromTable();

        if (graphRangeUpdated) {
          await this.loadDailyStats();
        }
      }
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load file list';
      });
      console.error('Error loading file list:', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
        this.isDataLoading = false;
      });
    }
  };

  loadFileDetail = async (fileId: string) => {
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });
    
    try {
      const fileDetail = await apiService.getUploadHistoryFileDetail(fileId);
      runInAction(() => {
        this.selectedFile = fileDetail;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Failed to load file details';
      });
      console.error('Error loading file detail:', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedFile = (file: FileHistoryDetail | null) => {
    this.selectedFile = file;
  };

  openFileDetail = async (fileId: string) => {
    runInAction(() => {
      this.reprocessMessage = null;
    });
    await this.loadFileDetail(fileId);
  };

  closeFileDetail = () => {
    this.selectedFile = null;
    this.reprocessMessage = null;
  };

  clearReprocessMessage = () => {
    this.reprocessMessage = null;
  };

  // Admin-only: reprocess a single failed/skipped file, then refresh list + detail.
  reprocessFile = async (fileId: string): Promise<boolean> => {
    runInAction(() => {
      this.isReprocessing = true;
      this.reprocessMessage = null;
      this.error = null;
    });

    try {
      const response = await apiService.reprocessFailedFiles([fileId]);
      const result = response.results?.[0];

      runInAction(() => {
        if (result?.ok) {
          this.reprocessMessage =
            `Reprocessed: ${result.old_status} → ${result.new_status}` +
            (result.claims ? ` (${result.claims} claims)` : '');
        } else {
          this.reprocessMessage = `Reprocess failed: ${result?.error || 'unknown error'}`;
        }
      });

      // Refresh the table and the open detail to reflect the new status/log.
      await this.loadFileList(undefined, this.fileListPage);
      await this.loadFileDetail(fileId);
      await this.loadDailyStats();

      return Boolean(result?.ok);
    } catch (error) {
      runInAction(() => {
        this.reprocessMessage = error instanceof Error ? error.message : 'Failed to reprocess file';
      });
      console.error('Error reprocessing file:', error);
      return false;
    } finally {
      runInAction(() => {
        this.isReprocessing = false;
      });
    }
  };
}

